import { Transaction, captureError, createTransaction } from "client/utils/apm";
import { AmpSEOMetadata } from "types/amplience";

interface AmplienceErrBody {
  type: string;
  message: string;
  data: Record<string, unknown>;
}

class AmplienceError extends Error {
  type: string;
  data: Record<string, unknown>;

  constructor(error: AmplienceErrBody) {
    super(error.message);
    this.type = error.type;
    this.data = error.data;
  }
}

const getContent = async <T extends AmpSEOMetadata>(
  url: string,
  { transaction, ignoreError }: { transaction?: Transaction; ignoreError?: boolean },
) => {
  const span = transaction?.startSpan(`GET ${url}`, "external.http");
  try {
    const res = await fetch(url, { redirect: "follow" });
    const body = await res.json();

    if (!res.ok) throw new AmplienceError(body);

    return body as { content: T };
  } catch (error) {
    if (!ignoreError) captureError(error);
    return null;
  } finally {
    span?.end();
  }
};

export const getSEOMetadata = async (deliveryKey: string): Promise<AmpSEOMetadata | undefined> => {
  const transaction = createTransaction("Get SEO Content");

  const basePath = window.__seo_metadata_initial_state__.baseCDNPath;
  if (!basePath) {
    captureError(new Error("missing cdn base path"));
    return;
  }

  const url = new URL(`/content/key/${deliveryKey}`, basePath);
  const res = await getContent<AmpSEOMetadata>(url.toString(), { transaction, ignoreError: true });

  transaction?.end();
  return res?.content;
};
